import { InputRadio, ItandiChip, RiFlagLine } from "@itandi/itandi-bb-ui";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useSubtypes } from "~/hooks/useSubtypes";
import type { DealType } from "~/models/Deal";
import type { DealParam, RelatedDealParam } from "~/models/DealForm";
import { ItandiAccountContext } from "~/models/ItandiAccount";
import type { Subtype } from "~/models/Subtype";
import Styles from "./Block.module.scss";

type Props = {
	isRelatedDeal?: boolean;
};

export const DealTypeBlock: React.FC<Props> = ({ isRelatedDeal = false }) => {
	const { data: subtypes } = useSubtypes().useIndex();
	const { company } = React.useContext(ItandiAccountContext);
	const { formState, watch, setValue, register } = useFormContext<
		DealParam | RelatedDealParam
	>();
	const { errors } = formState;
	const dealType = watch("dealType");
	const handleChangeDealType = (value: DealType): void => {
		setValue("dealType", value);
		if (!isRelatedDeal && value === "restoration") {
			setValue("inspectionBy", company?.defaultInspectionBy ?? "vendor");
		} else {
			setValue("inspectionBy", "none");
		}
		setValue("dealSubtype", null);
	};
	if (subtypes === null) {
		return null;
	}
	return (
		<div className={Styles.Block}>
			<div className={Styles.BlockCaptionWrapper}>
				<div className={Styles.BlockCaption}>
					<span className={Styles.BlockIconWrapper}>
						<RiFlagLine className={Styles.BlockIcon} />
					</span>
					<span className={Styles.BlockCaptionLabel}>案件タイプ</span>
				</div>
			</div>
			<div className={Styles.BlockForms}>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>案件の種類</span>
						<span className={Styles.RequiredLabel}>
							<ItandiChip label="必須" />
						</span>
					</div>
					<div className={Styles.FormContent}>
						<div className={Styles.InputRadioGroup}>
							<InputRadio
								checked={dealType === "restoration"}
								isInvalid={errors.dealType != null}
								label="原状回復"
								name="dealType"
								onChange={(): void => {
									handleChangeDealType("restoration");
								}}
								ref={register("dealType").ref}
								value="restoration"
							/>
							<InputRadio
								checked={dealType === "repair"}
								isInvalid={errors.dealType != null}
								label="修繕"
								name="dealType"
								onChange={(): void => {
									handleChangeDealType("repair");
								}}
								value="repair"
							/>
							{subtypes.length > 0 && (
								<InputRadio
									checked={dealType === "other"}
									isInvalid={errors.dealType != null}
									label="その他"
									name="dealType"
									onChange={(): void => {
										handleChangeDealType("other");
									}}
									value="other"
								/>
							)}
						</div>
						{errors.dealType != null && (
							<div className={Styles.ErrorMessage}>
								{errors.dealType.message}
							</div>
						)}
					</div>
				</div>
				{watch("dealType") === "other" && (
					<div className={Styles.FormRow}>
						<div className={Styles.FormLabel}>
							<span className={Styles.FormLabelText}>案件のサブタイプ</span>
							<span className={Styles.RequiredLabel}>
								<ItandiChip label="必須" />
							</span>
						</div>
						<div className={Styles.FormContent}>
							<div className={Styles.InputRadioGroup}>
								{subtypes?.map((subtype: Subtype) => (
									<InputRadio
										checked={watch("dealSubtype")?.id === subtype.id}
										isInvalid={errors.dealSubtype != null}
										key={subtype.id}
										label={subtype.name}
										name="dealSubtype"
										onChange={(): void => {
											setValue("dealSubtype", subtype);
										}}
										ref={register("dealSubtype").ref}
										value={subtype.id}
									/>
								))}
							</div>
							{errors.dealType != null && (
								<div className={Styles.ErrorMessage}>
									{errors.dealType.message}
								</div>
							)}
							{errors.dealSubtype != null && (
								<div className={Styles.ErrorMessage}>
									{errors.dealSubtype.message}
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
